import { memo } from 'react';

import { Row } from '@tanstack/react-table';

import {
  ScrollableCell,
  ScrollableCellWithAvatar,
  ScrollableCellWithColorDot,
} from '@/components/data-table/cells';
import { formatWallClockDate, formatWallClockDateTime } from '@/utils/date-formatters';

import { EmptyDescriptionScope } from '../../../components/modals/empty-description-info-modal';
import { hasInvalidApprovedInputForAggregateRow } from '../../../helpers/invalid-approved-inputs.helper';
import { useApprovalsStore } from '../../../store/approvals.store';
import { ApprovalsTableMeta } from '../../../types/approvals-table.types';
import { ApprovalProjectRow } from '../../../types/time-tracking-approvals';
import {
  asMemberRow,
  asTimeEntry,
  asTimeOffEntry,
  isLoadMoreTableRow,
  isMemberRow,
  isTimeEntryRow,
  isTimeOffRow,
} from './approval-row.helpers';
import { EmptyDescriptionWarningIcon } from './empty-description-warning-icon';
import { InvalidApprovedInputWarningIcon } from './invalid-approved-input-warning-icon';
import { RowExpandToggle } from './row-expand-toggle';

const FALLBACK_PROJECT_DOT_COLOR = 'hsl(var(--muted-foreground))';

interface ApprovalNameCellProps {
  row: Row<ApprovalProjectRow>;
  meta: ApprovalsTableMeta | undefined;
  onShowEmptyDescriptionInfo: (scope: EmptyDescriptionScope) => void;
  /**
   * Live expansion state, passed as props (not read via `row.getIsExpanded()`) so this cell
   * can be `React.memo`'d: memo compares props, so reading live row methods would make the
   * chevron miss expand/collapse updates. With these as props, memo re-renders on expand but
   * NOT on an unrelated row's selection toggle — which is what stops the avatar from blinking.
   * (Toggling itself reads `row` directly — that's a click handler, not a render-time read,
   * so it doesn't affect memoization.)
   */
  isExpanded: boolean;
  canExpand: boolean;
}

/**
 * The "Project & Member" cell — the table's primary, expandable column.
 *
 * Renders per row depth:
 *   - time entry → the entry date (indented under its member),
 *   - member     → expand toggle + name with avatar,
 *   - project    → expand toggle + name with color dot and logo.
 * Member and project rows also surface the empty-description warning.
 */
function ApprovalNameCellComponent({
  row,
  meta,
  onShowEmptyDescriptionInfo,
  isExpanded,
  canExpand,
}: ApprovalNameCellProps) {
  const isLoading = meta?.loadingRowIds.has(row.id) ?? false;
  // A boolean, not the map: subscribing to the map would re-render every name cell in the table
  // each time any input flips validity, because the map is replaced wholesale.
  const hasInvalidApprovedInputBelow = useApprovalsStore(state =>
    hasInvalidApprovedInputForAggregateRow(state.invalidApprovedInputs, row.id)
  );

  // Load-more sentinels render via the table's full-width row override, not per-column —
  // guard here so this cell is sentinel-safe even if that override is ever bypassed.
  if (isLoadMoreTableRow(row)) {
    return null;
  }

  if (isTimeOffRow(row)) {
    // Time-off days carry no meaningful time component — show the plain date only.
    // Wall-clock formatter (like worklog entries): the backend `date` is a `...Z` value
    // that must render literally, not shifted into the viewer's timezone.
    return (
      <ScrollableCell
        className="flex items-center gap-2 pl-12"
        text={formatWallClockDate(asTimeOffEntry(row).date)}
      />
    );
  }

  if (isTimeEntryRow(row)) {
    return (
      <ScrollableCell
        className="flex items-center gap-2 pl-12"
        text={formatWallClockDateTime(asTimeEntry(row).date)}
      />
    );
  }

  if (isMemberRow(row)) {
    const member = asMemberRow(row);

    return (
      <div className="flex items-center gap-2 pl-6">
        <RowExpandToggle
          canExpand={canExpand}
          isExpanded={isExpanded}
          isLoading={isLoading}
          onToggle={() => row.toggleExpanded()}
        />
        {member.hasEmptyDescription && (
          <EmptyDescriptionWarningIcon onClick={() => onShowEmptyDescriptionInfo('member')} />
        )}
        <ScrollableCellWithAvatar
          text={member.member.name}
          image={member.member.avatarUrl}
          avatarName={member.member.name}
          alt={`${member.member.name} avatar`}
        />
        {hasInvalidApprovedInputBelow && <InvalidApprovedInputWarningIcon />}
      </div>
    );
  }

  const { project } = row.original;

  return (
    <div className="flex items-center gap-2">
      <RowExpandToggle
        canExpand={canExpand}
        isExpanded={isExpanded}
        isLoading={isLoading}
        onToggle={() => row.toggleExpanded()}
      />
      {row.original.hasEmptyDescription && (
        <EmptyDescriptionWarningIcon onClick={() => onShowEmptyDescriptionInfo('project')} />
      )}
      <ScrollableCellWithColorDot
        text={project.projectName}
        image={project.logoUrl}
        avatarName={project.projectName}
        alt={`${project.projectName} logo`}
        dotColor={project.colorHex || FALLBACK_PROJECT_DOT_COLOR}
        className="font-medium"
      />
      {hasInvalidApprovedInputBelow && <InvalidApprovedInputWarningIcon />}
    </div>
  );
}

/**
 * Memoized so toggling one row's selection doesn't re-render every name cell (which would
 * remount avatars and make them blink). Re-renders only when its own props change: expansion
 * (`isExpanded`/`canExpand`), first-page loading (`meta`), or the row data itself.
 */
export const ApprovalNameCell = memo(ApprovalNameCellComponent);
